import type {
  SimpleActivityResponse,
  CompoundedActivityResponse,
  CompoundedProgress,
  SimpleProgress,
  ActivityQuery,
} from '~/models/Activity'
import { activityClient } from '~/api/client/activityClient'
import { xApiClient } from '~/api/client/xApiClient'
import { contentClient } from '~/api/client/contentClient'
import { useSchoolYear } from '~/composables/useSchoolYear'
import { xAPIParams, xAPIStatement } from '~/models/xAPIStatement'

const environment = import.meta.env.VITE_ACTIVITY_API_ENV
const { currentSchoolYearInterval } = useSchoolYear()

// Expected format: 2022-08-01
const fromDate = currentSchoolYearInterval.start!.toISODate({ format: 'extended' })

const validateStatus = (status: number) => status >= 200 && status < 300

export default function useActivityApi() {
  const getCompoundProgress = async (userIds: string[], locationIds: number[]): Promise<CompoundedProgress> => {
    const queryObject = JSON.stringify(<ActivityQuery>{
      users: userIds,
      pageIDs: locationIds.map(String),
    })

    const { data } = <CompoundedActivityResponse>await activityClient.get(
      '/progress/compounded',
      {
        validateStatus,
        params: {
          query: queryObject,
          env: environment,
          fromDate,
        },
      },
    )

    return data
  }

  const getSimpleProgress = async (userIds: string[], locationIds: number[]): Promise<SimpleProgress> => {
    const queryObject = JSON.stringify(<ActivityQuery>{
      users: userIds,
      pageIDs: locationIds.map(String),
    })

    const { data } = <SimpleActivityResponse>await activityClient
      .get(
        '/progress/simple',
        {
          validateStatus,
          params: {
              query: queryObject,
              env: environment,
              fromDate,
          },
        },
      )
    return data
  }

  const loadStatement = async (locationId: number, params: xAPIParams = {}): Promise<xAPIStatement> => {
    // @ts-ignore - URLSearchParams says that it requires Record<string, string> but it also accepts Record<string, number>
    const queryParams = (new URLSearchParams(params)).toString()
    const { data } = await contentClient.get(`/activity/statement/${locationId}?${queryParams}`)
    return data
  }

  const postStatement = (statement: xAPIStatement) => xApiClient.post('/statements', statement)

  return {
    getSimpleProgress,
    getCompoundProgress,
    loadStatement,
    postStatement,
  }
}
